import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {delay, map, of, Subject, take, takeUntil} from "rxjs";
import {HtmlHelper} from "../../helpers/html.helper";
import {PointerPositionHelper} from "../../helpers/pointer-position.helper";
import {AbstractLitElement} from "../abstract-lit.element";

@customElement(`ha-popover`)
export class HaPopoverComponent extends AbstractLitElement {

    private _resize$ = new Subject<ResizeObserverEntry>();
    private _resizeObserver = new ResizeObserver(entries => entries.forEach(entry => this._resize$.next(entry)));
    private _lastPositionKey = '';

    private _content: unknown = html``;

    @property({type: Object})
    public get content(): unknown {
        return this._content;
    }

    public set content(content: unknown) {
        if (!content) {
            return;
        }

        const oldContent = this._content;
        this._content = content;
        this.requestUpdate(`content`, oldContent);
    }

    public connectedCallback() {
        super.connectedCallback();
        this._resizeObserver.observe(this);
        this._resize$.pipe(
            takeUntil(this._disconnected$),
            map(({contentRect}) => ({width: contentRect.width, height: contentRect.height})),
        ).subscribe(entry => this._calculatePosition(entry.width, entry.height))

        of(void 0).pipe(
            delay(250),
            take(1),
            takeUntil(this._disconnected$),
        ).subscribe(() => this.classList.add(`fade-in`));
    }

    public disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver.unobserve(this);
        this._resizeObserver.disconnect();
    }

    protected render(): unknown {
        return this.content;
    }

    /**
     * Positions the popover on the opposite horizontal half of the viewport.
     * If the mouse is on the right half, the popover appears centered on the left half,
     * and vice versa. Vertically centered within the viewport.
     */
    private _calculatePosition(width: number, height: number): void {
        // Skip positioning before content has rendered
        if (width === 0 || height === 0) {
            return;
        }

        // Read from the JavaScript holder, not from <html>: by the time this runs (from the
        // ResizeObserver) the latest mousemove has been processed there, while the value on <html>
        // is only the one written when the popover opened.
        const mouseX = PointerPositionHelper.x ?? 0;

        const browserWidth = document.body.getBoundingClientRect().width;
        const browserHeight = document.body.getBoundingClientRect().height;
        const halfW = browserWidth / 2;
        const padding = 16;

        const mouseInRight = mouseX > halfW;
        const maxW = halfW - 2 * padding;
        const maxH = browserHeight - 2 * padding;
        const clampedW = Math.min(width, maxW);
        const clampedH = Math.min(height, maxH);

        const targetX = mouseInRight ? 0 : halfW;
        const popoverX = targetX + (halfW - clampedW) / 2;
        const popoverY = (browserHeight - clampedH) / 2;

        // Only write CSS variables when values actually changed
        const key = `${popoverX}|${popoverY}|${maxW}|${maxH}`;
        if (key === this._lastPositionKey) {
            return;
        }
        this._lastPositionKey = key;

        HtmlHelper.setGlobalVar(`popover-x`, `${popoverX}px`);
        HtmlHelper.setGlobalVar(`popover-y`, `${popoverY}px`);
        HtmlHelper.setGlobalVar(`popover-width`, `${maxW}px`);
        HtmlHelper.setGlobalVar(`popover-height`, `${maxH}px`);
    }
}